/**
 * English translation bundle.
 *
 * The shape of this object IS the contract. Add a new key here first, then
 * other locales mirror it. Nested keys map to dotted paths in t().
 *
 * Only user-facing strings live here. Things that look like content but are
 * actually data (a status name from the DB, a currency code, etc.) stay
 * on the wire as data and are rendered as-is.
 */
export const en = {
  common: {
    appName: 'Utazzo',
    cancel: 'Cancel',
    save: 'Save',
    loading: 'Loading…',
    submit: 'Submit',
    backToLogin: 'Back to sign in',
    requiredField: 'This field is required',
  },
  auth: {
    login: {
      title: 'Sign in to your account',
      subtitle: 'Continue to {{appName}}',
      emailLabel: 'Email or CallerDesk number',
      emailPlaceholder: 'you@company.com or 09111111890',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      rememberMe: 'Remember me on this device',
      forgotPassword: 'Forgot password?',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      submit: 'Sign in',
      submitting: 'Signing in…',
      noAccountHint: 'New here? Contact your administrator for access.',
      inactivityTitle: 'Signed out due to inactivity',
      inactivityBody:
        'For your security you were signed out after a period of inactivity. Please sign in again.',
      invalidCredentials: 'Incorrect email or password',
      genericError: 'Something went wrong. Please try again.',
    },
    forgot: {
      title: 'Reset your password',
      subtitle: 'We will email you a link to set a new password.',
      emailLabel: 'Work email',
      submit: 'Send reset link',
      submitting: 'Sending…',
      success: 'If an account exists for that email, a reset link is on its way.',
    },
    reset: {
      title: 'Choose a new password',
      subtitle: 'Pick something strong you have not used elsewhere.',
      newPasswordLabel: 'New password',
      confirmPasswordLabel: 'Confirm new password',
      strength: 'Strength:',
      submit: 'Save new password',
      submitting: 'Saving…',
      missingToken: 'This reset link is invalid or has expired. Please request a new one.',
      success: 'Password updated. You can sign in with your new password.',
    },
    logout: {
      label: 'Sign out',
      confirm: 'Sign out of {{appName}}?',
    },
  },
  shell: {
    nav: {
      welcome: 'Welcome Utazzer',
      dashboard: 'Dashboard',
      'website-dashboard': 'Website Dashboard',
      'products-dashboard': 'Products Dashboard',
      leads: 'Leads',
      'converted-leads': 'Converted leads',
      'fund-requests': 'Fund requests',
      'ops-approvals': 'Operations approvals',
      'lead-parsing': 'Lead Parsing System',
      'lead-reports': 'Reports',
      'marketing-reports': 'Marketing Reports',
      callsense: 'CallSense',
      'lead-approvals': 'Pending approvals',
      'exit-unassigned': 'Unassigned',
      'catalog-approvals': 'Product approvals',
      'website-approvals': 'Content approvals',
      'lead-imports': 'Imports',
      scheduler: 'Scheduler Logs',
      status: 'Status',
      blogs: 'Blogs',
      pages: 'Custom Pages',
      career: 'Career',
      'welcome-page': 'Welcome Page',
      media: 'Media',
      navigation: 'Menus',
      destinations: 'Destinations',
      partners: 'Partners',
      press: 'Press',
      'customer-pdf': 'Customer Itinerary Link',
      'cities-cms': 'City Management | CMS',
      countries: 'Countries',
      states: 'States',
      currencies: 'Currencies',
      'theme-collections': 'Themes & Collections',
      'hero-inclusions': 'Hero Inclusions',
      'inclusions-exclusions': 'Inclusions & Exclusions',
      'query-types': 'Query Types',
      'query-sources': 'Query Sources',
      'query-sub-sources': 'Query Sub Sources',
      'query-mediums': 'Query Mediums',
      'query-statuses': 'Query Statuses',
      'query-sub-statuses': 'Query Sub Statuses',
      customers: 'Customers',
      'ticketing-action': 'Action needed',
      'ticketing-flights': 'Flights',
      'ticketing-hotels': 'Hotels',
      'ticketing-drop-offs': 'Drop-offs',
      'ticketing-activities': 'Activities',
      'ticketing-reports': 'Reports',
      coupons: 'Coupons',
      discounts: 'Discounts',
      'additional-discounts': 'Upsell',
      'agent-targets': 'Agent Targets',
      'lost-reasons': 'Lost Reasons',
      'marketing-cost-heads': 'Marketing Cost Heads',
      departments: 'Departments',
      'sub-branches': 'Sub Branches',
      'call-statuses': 'Call Statuses',
      'call-sub-statuses': 'Call Sub Statuses',
      suppliers: 'Suppliers',
      airlines: 'Airlines',
      hotels: 'Hotels',
      cruises: 'Cruises',
      activities: 'Activities',
      markups: 'Markups',
      'markup-profiles': 'Markups',
      branches: 'Branches',
      'meal-plans': 'Meal Plans',
      'room-types': 'Room Types',
      'occupancy-types': 'Occupancy Types',
      'vehicle-types': 'Vehicle Types',
      languages: 'Languages',
      cuisines: 'Cuisines',
      locations: 'Locations',
      transfers: 'Transfers',
      visas: 'Visas',
      insurance: 'Insurance',
      'meal-services': 'Meal Services',
      packages: 'Packages',
      'destination-pages': 'Destination Pages',
      'category-pages': 'Category Pages',
      'tourism-boards': 'Tourism Boards',
      'landing-pages': 'Landing Pages',
      'best-time-to-visit': 'Best Time to Visit',
      'best-places-to-visit': 'Best Places to Visit',
      itineraries: 'Itinerary Dashboard',
      'itinerary-reprice-approvals': 'Reprice Approvals',
      'itinerary-void-approvals': 'Void Approvals',
      'fixed-departures': 'Fixed Departures',
      tbo: 'TBO Geography',
      secrets: 'Secrets Manager',
      'meta-capi': 'Meta CAPI',
      settings: 'Settings',
      notifications: 'Notifications',
      users: 'Users',
      roles: 'Roles',
      'session-audit': 'Session Audit',
      'tbo-audit': 'TBO API Audit',
      'whatsapp-log': 'WhatsApp Log',
      whatsapp: 'WhatsApp',
      invoices: 'Invoices',
      'sales-register': 'Sales Register',
      'client-outstanding': 'Client Outstanding',
      'supplier-paid-outstanding': 'Supplier Paid / Client Due',
      'balance-due': 'Balance Due',
      'bank-statement': 'Bank Transaction Statement',
      'operation-pending': 'Operation Pending',
      'deposit-booking': 'Deposit Booking',
      'supplier-payments': 'Supplier Payment Status',
      'agent-report-card': 'Agent Report Card',
      'monthly-sale': 'Monthly Sale',
      'bank-reconcile': 'Bank Reconciliation',
      'supplier-payment-schedule': 'Payment Schedule',
      'supplier-ledger': 'Supplier Ledger',
      'gst-output-register': 'GST Output Register',
      'tcs-register': 'TCS Register',
      'credit-notes': 'Credit Notes',
      'supplier-change-approvals': 'Supplier Change Approvals',
      'destinations-master': 'Destinations',
      'trip-types': 'Trip Types',
      'followup-reasons': 'Follow-up Reasons',
      'blog-categories': 'Blog Interests',
      section: {
        overview: 'Overview',
        operations: 'Operations',
        ticketing: 'Ticketing',
        inventory: 'Inventory & supply',
        'itinerary-masters': 'Itinerary masters',
        crm: 'Lead & CRM masters',
        global: 'Global masters',
        website: 'Website',
        finance: 'Finance',
        'financial-reports': 'Financial Reports',
        system: 'System',
      },
    },
    sidebar: {
      brandSub: 'CRM',
      collapse: 'Collapse sidebar',
      expand: 'Expand sidebar',
      locked: 'You do not have access',
    },
    topbar: {
      home: '',
      search: 'Search or jump to…',
      notifications: 'Notifications',
      help: 'Help',
      viewSite: 'View site',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      userMenu: 'User menu',
    },
    command: {
      placeholder: 'Search pages, settings, or jump to…',
      empty: 'No results found.',
      navigate: 'Navigate',
      hintNav: 'navigate',
      hintSelect: 'select',
      hintClose: 'close',
    },
    breadcrumbs: { home: 'Home' },
    user: { profile: 'My profile', preferences: 'Preferences' },
  },
  errors: {
    notFound: {
      title: 'Page not found',
      message: "We couldn't find what you were looking for.",
      cta: 'Go back home',
    },
    crash: {
      title: 'Something went wrong',
      message:
        'The page hit an unexpected error. Reloading usually helps. If it keeps happening, contact support.',
      cta: 'Reload',
    },
  },
} as const;

export type Translations = typeof en;
